/**
 * SaveSubjectDialog.jsx — "that's Maya, remember her".
 *
 * Turns a picture already in the conversation into a cast member, so the next
 * picture can have the same face (or the same mug) in it. Deliberately two
 * fields: a name to call them by and what they are.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import * as L from "lucide-react";

const KINDS = [
  { id: "person", label: "Person" },
  { id: "pet", label: "Pet" },
  { id: "product", label: "Product" },
  { id: "place", label: "Place" },
  { id: "style", label: "Style" },
];

const toBase64 = async (src) => {
  if (typeof src !== "string") return "";
  if (src.startsWith("data:")) return src.split(",")[1];
  const r = await fetch(src, { credentials: "include" });
  const blob = await r.blob();
  return await new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onerror = rej;
    fr.onload = () => res(String(fr.result).split(",")[1]);
    fr.readAsDataURL(blob);
  });
};

export default function SaveSubjectDialog({ imageSrc, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState("person");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const save = async () => {
    const n = name.trim();
    if (!n) return toast.error("Give them a name — that's how you'll ask for them.");
    setBusy(true);
    try {
      const photo = await toBase64(imageSrc);
      const { data } = await api.post("/cast", { name: n, kind, photos: [photo] });
      toast.success(`${n} is in the cast — just say their name in your next picture.`);
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that subject.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/45 backdrop-blur-sm"
         onClick={() => !busy && onClose?.()} data-testid="save-subject-dialog">
      <div className="w-full max-w-sm rounded-3xl bg-[var(--rammy-surface)] border border-[var(--rammy-border)] p-5 space-y-4"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <img src={imageSrc} alt="" className="w-16 h-16 rounded-xl object-cover border border-[var(--rammy-border)]"/>
          <div>
            <h3 className="font-display text-xl leading-tight">Remember this one?</h3>
            <p className="text-xs text-[var(--rammy-text-sec)] mt-1">
              I'll keep them looking like themselves in every picture after this.
            </p>
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[var(--rammy-text-muted)]">
            What should I call them?
          </label>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); }}
            placeholder="Maya"
            maxLength={40}
            className="mt-1.5 w-full rounded-xl border border-[var(--rammy-border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--rammy-cyan)]"
            data-testid="save-subject-name"
          />
        </div>

        <div className="inline-flex flex-wrap gap-1.5">
          {KINDS.map(k => (
            <button
              key={k.id}
              onClick={() => setKind(k.id)}
              data-active={kind === k.id ? "true" : "false"}
              data-testid={`save-subject-kind-${k.id}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                kind === k.id
                  ? "border-[var(--rammy-cyan)] bg-[var(--rammy-cyan)]/10 text-[var(--rammy-text)]"
                  : "border-[var(--rammy-border)] text-[var(--rammy-text-sec)]"
              }`}
            >
              {k.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 pt-1">
          <Button onClick={save} disabled={busy} className="flex-1" data-testid="save-subject-submit">
            {busy ? <><L.Loader2 size={15} className="mr-1.5 animate-spin"/>Saving…</> : "Add to cast"}
          </Button>
          <Button variant="ghost" onClick={() => onClose?.()} disabled={busy} data-testid="save-subject-cancel">
            Not now
          </Button>
        </div>
      </div>
    </div>
  );
}
